import React, { useEffect, useState } from "react";
import {
  TextInput,
  SimpleForm,
  required,
  ReferenceInput,
  AutocompleteInput,
  TopToolbar,
  Button,
  useDataProvider,
  EditProps,
  NumberInput,
  maxValue,
  minValue,
  BooleanInput,
  SelectInput,
  FormDataConsumer,
} from "react-admin";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { GetParam } from "utils/tools";
import SanitizedGrid from "components/SanitizedGrid";
import { Grid, makeStyles, Typography } from "@material-ui/core";
import CustomEditToolbar from "components/CustomEditToolbar";
import CustomEndDateInput from "components/CustomEndDateInput";
import CustomRegistrationDateInput, {
  EType,
} from "components/CustomRegistrationDateInput";
import CustomDatePickerInput from "./components/CustomDatePickerInput";
import { reject } from "lodash";
import moment from "moment";
import { useField } from "react-final-form";
import EditWrapper from "../../components/EditWrapper";
import CustomTooltip from "../../components/CustomTooltip";
import InfoIcon from "@material-ui/icons/Info";

interface ICustomReferenceInput {
  schoolId: string;
}

const useStyles = makeStyles({
  captionTooltip: {
    marginTop: "5px",
    marginBottom: "5px",
  },
  captionText: {
    display: "flex",
    flexDirection: "row",
    width: "100%",
    gap: "2px",
  },
});

const CustomReferenceInput = (props: ICustomReferenceInput) => {
  const startedAt = useField("startDate");
  const endedAt = useField("endDate");
  const earlyStartDate = useField("earlyRegistrationStartDate");
  const earlyEndDate = useField("earlyRegistrationEndDate");
  const lateStartDate = useField("lateRegistrationStartDate");

  return (
    <ReferenceInput
      label="Select Academic Year"
      source="regularAcademicYearId"
      reference="academicYears"
      validate={[required()]}
      filter={{ SchoolId: Number(props.schoolId) }}
      onChange={() => {
        startedAt.input.onChange("");
        endedAt.input.onChange("");
        earlyStartDate.input.onChange("");
        earlyEndDate.input.onChange("");
        lateStartDate.input.onChange("");
      }}
    >
      <SelectInput optionText="name" optionId="id" />
    </ReferenceInput>
  );
};

const AcademicYearEdit = (props: EditProps) => {
  const [academicYears, setAcademicYears] = useState<ICcaAcademicYear[]>([]);
  const [idAcademicYear, setIdAcademicYear] = useState<number>(0);
  const [regularAcademicyear, setRegularAcademicyear] =
    useState<IAcademicYear>();
  const provider = useDataProvider();
  const param = GetParam();
  const classes = useStyles();
  const { id } = props;

  //fetch area data for current school on first load
  useEffect(() => {
    provider
      .getList("ccaAcademicYears", {
        filter: { SchoolId: Number(param.get("schoolId")) },
        pagination: {
          page: 1,
          perPage: 1000,
        },
        sort: {
          field: "Id",
          order: "ASC",
        },
      })
      .then(({ data }) => {
        setAcademicYears(data as ICcaAcademicYear[]);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (idAcademicYear && idAcademicYear > 0) {
      provider
        .getOne("academicYears", {
          id: idAcademicYear,
        })
        .then(({ data }) => {
          setRegularAcademicyear(data as IAcademicYear);
        });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [idAcademicYear]);

  const breadcrumb = [
    { link: `/schools`, title: "Schools" },
    {
      link: `/schools/${param.get("schoolId")}/show/4`,
      title: "Academic Years",
    },
    {
      link: ``,
      title: id,
    },
  ];

  const EditActions = (props: any) => {
    const { history } = props;
    return (
      <TopToolbar>
        <Button
          onClick={() =>
            history.push(
              `/ccaAcademicYears/${props.data?.id}/show?schoolId=${Number(
                props.data?.schoolId,
              )}`,
            )
          }
          label="Show"
        >
          <VisibilityIcon />
        </Button>
      </TopToolbar>
    );
  };

  let excludeDates: IExcludeDates[] = [];
  let academicYearsWithoutSelected = reject(academicYears, {
    id: Number(id),
  });

  academicYearsWithoutSelected.forEach((academicYear: ICcaAcademicYear) => {
    let temp: IExcludeDates = {
      startDate: academicYear.startDate,
      endDate: academicYear.endDate,
    };

    excludeDates.push(temp);
  });

  const academicYearInputCallback = (id: any) => {
    setIdAcademicYear(id);
  };

  const transform = (data: any) => {
    if (data.allowLateRegistration && !data.lateRegMinDaysOffset) {
      data.lateRegMinDaysOffset = 14;
    }

    return data;
  };

  return (
    <>
      <EditWrapper
        breadcrumb={breadcrumb}
        customName={{
          resourceName: "CCA Academic Year",
          propertyName: "name",
        }}
        actions={<EditActions history={props.history} />}
        transform={transform}
        {...props}
      >
        <SimpleForm
          toolbar={
            <CustomEditToolbar
              redirectLink={`/schools/${param.get("schoolId")}/show/4`}
            />
          }
        >
          <SanitizedGrid container spacing={2}>
            <Grid item xs={12}>
              <Typography variant="h6">
                School & Regular Academic Year
              </Typography>
            </Grid>
            <Grid item xs={4}>
              <ReferenceInput
                label="Select School"
                source="schoolId"
                reference="schools"
                disabled
              >
                <AutocompleteInput optionText="name" />
              </ReferenceInput>
            </Grid>
            <Grid item xs={8}></Grid>

            <FormDataConsumer>
              {({ formData }) => {
                academicYearInputCallback(formData.regularAcademicYearId);
                return <></>;
              }}
            </FormDataConsumer>

            <Grid item xs={4}>
              <CustomReferenceInput schoolId={param.get("schoolId")} />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={12}>
              <Typography variant="h6">Details</Typography>
            </Grid>

            <Grid item xs={4}>
              <TextInput
                source="name"
                label="Academic Year"
                validate={required()}
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <Grid item xs={4}>
              <CustomDatePickerInput
                name="startDate"
                label="Start Date"
                min={moment
                  .utc(regularAcademicyear?.startDate)
                  .format("yyyy-MM-DD")}
                max={moment
                  .utc(regularAcademicyear?.endDate)
                  .format("yyyy-MM-DD")}
                excludeDates={excludeDates}
                isReset
              />
            </Grid>

            <Grid item xs={4}>
              <CustomEndDateInput
                source="endDate"
                label="End Date"
                parentEndDate={moment
                  .utc(regularAcademicyear?.endDate)
                  .format("yyyy-MM-DD")}
                comparison="startDate"
                excludeDates={excludeDates}
              />
            </Grid>
            <Grid item xs={4}></Grid>

            <Grid item xs={4}>
              <CustomRegistrationDateInput
                source="earlyRegistrationStartDate"
                type={EType.Early}
                label="Early Registration Start Date"
              />
            </Grid>
            <Grid item xs={4}>
              <CustomRegistrationDateInput
                source="earlyRegistrationEndDate"
                comparison="earlyRegistrationStartDate"
                type={EType.Early}
                label="Early Registration End Date"
              />
            </Grid>
            <Grid item xs={4}>
              <CustomRegistrationDateInput
                source="lateRegistrationStartDate"
                type={EType.Late}
                label="Late Registration Start Date"
              />
            </Grid>

            <Grid item xs={4}>
              <BooleanInput
                source="allowLateRegistration"
                label="Allow Late Registration"
              />
            </Grid>
            <Grid item xs={8}></Grid>

            <FormDataConsumer>
              {({ formData }) => {
                if (formData.allowLateRegistration) {
                  return (
                    <>
                      <Grid item xs={4} style={{ marginTop: "-35px" }}>
                        <NumberInput
                          source="lateRegMinDaysOffset"
                          label="Late Registration Minimum Days Offset (optional)"
                          validate={[minValue(0), maxValue(100)]}
                        />
                        <CustomTooltip
                          title={
                            <>
                              <Typography variant="caption">
                                If the Late Registration Minimum Days Offset is
                                set to {formData.lateRegMinDaysOffset || 14}, it
                                means that when a parent registers after the
                                Late Registration Start Date, they can only
                                register for dates at least{" "}
                                {formData.lateRegMinDaysOffset || 14} days from
                                the registration date.
                              </Typography>
                            </>
                          }
                          aria-label="Late Registration Minimum Days Offset"
                          classes={{
                            tooltip: classes.captionTooltip,
                          }}
                          placement="bottom-start"
                        >
                          <div className={classes.captionText}>
                            <InfoIcon fontSize="small" color="secondary" />
                            <Typography
                              variant="caption"
                              style={{ cursor: "default" }}
                            >
                              This field is optional. If left blank, a default
                              value of 14 days will be applied.
                            </Typography>
                          </div>
                        </CustomTooltip>
                      </Grid>
                      <Grid item xs={8}></Grid>
                    </>
                  );
                }
                return <></>;
              }}
            </FormDataConsumer>

            <Grid item xs={12}>
              <Typography variant="h6">Refund Rate (%)</Typography>
            </Grid>

            <Grid item xs={4}>
              <NumberInput
                source="refundRate"
                label="Refund Rate (%)"
                validate={[required(), minValue(0), maxValue(100)]}
              />
            </Grid>
          </SanitizedGrid>
        </SimpleForm>
      </EditWrapper>
    </>
  );
};

export default AcademicYearEdit;
